import {
  RolodexEntryJson,
  RolodexEntryType,
} from 'types/rolodex/rolodexEntries'
import { parseArray, parseNumber, parseString } from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'
import { type ConsequenceMatrixSelection } from './geckoRiskAssessmentConfig'

/** A risk selection (likelihood/consequence/class), used for both the scenario's
 *  current risk and the Gate 0 mitigated risk. */
export interface RiskScenarioRiskSelection {
  likelihood: number
  consequence: number
  riskClass: string
  riskScore: number
  consequenceMatrixSelections?: ConsequenceMatrixSelection[]
}

/** Typed parse for a persisted RiskScenarioRiskSelection bag (e.g. Gate 0
 *  mitigatedRisk), so malformed JSON can't leak NaN/wrong-typed fields into the
 *  risk math and Excel export. Returns undefined when the value isn't an object. */
export const parseRiskScenarioRiskSelection = (
  input: unknown
): RiskScenarioRiskSelection | undefined => {
  if (typeof input !== 'object' || input === null) return undefined
  const record = input as Record<string, unknown>
  return {
    likelihood: parseNumber(record.likelihood) ?? 0,
    consequence: parseNumber(record.consequence) ?? 0,
    riskClass: parseString(record.riskClass) ?? '',
    riskScore: parseNumber(record.riskScore) ?? 0,
    consequenceMatrixSelections: parseArray<ConsequenceMatrixSelection>(
      record.consequenceMatrixSelections
    ),
  }
}

export interface RiskScenarioData extends Record<string, unknown> {
  name: string
  description?: string
  likelihood: number
  consequence: number
  risk: number
  risk_class?: string
  risk_justification?: string
  consequence_matrix_selections?: ConsequenceMatrixSelection[]
  observation_ids?: string[]
}

export class GeckoRiskScenario extends GeckoEntryBase<
  RiskScenarioData,
  {
    organization: string
    site: string
    unit?: string
  }
> {
  private constructor(json: RolodexEntryJson) {
    super(
      json,
      {
        name: parseString(json.data.name) ?? '',
        description: parseString(json.data.description),
        likelihood: parseNumber(json.data.likelihood) ?? 0,
        consequence: parseNumber(json.data.consequence) ?? 0,
        risk: parseNumber(json.data.risk) ?? 0,
        risk_class: parseString(json.data.risk_class),
        risk_justification: parseString(json.data.risk_justification),
        consequence_matrix_selections: Array.isArray(
          json.data.consequence_matrix_selections
        )
          ? (json.data
              .consequence_matrix_selections as ConsequenceMatrixSelection[])
          : undefined,
        observation_ids: Array.isArray(json.data.observation_ids)
          ? json.data.observation_ids.filter(
              (id): id is string => typeof id === 'string'
            )
          : undefined,
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
        unit: parseString(json.tags.unit),
      },
      {}
    )
  }

  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoRiskScenario(json)
  }
}
